import Sidebar from '@/components/Home_Sidebar/sidebarView.vue';
import Navbar from '@/components/Home_Navbar/NavbarView.vue';
import { get_token, getCookie } from '@/router/index.js';

const USER_PAGE_SIZE = 10;

const PLATFORM_TABS = [
  { key: 'aic_one', label: 'AIC One', endpoint: 'get_audits_grouped_aic_one' },
  { key: 'orcaa', label: 'Orcaa', endpoint: 'get_audits_grouped_orcaa' },
  { key: 'engagenet', label: 'EngageNet', endpoint: 'get_audits_grouped_engagenet' },
  { key: 'workflows', label: 'Proba Workflows', endpoint: 'get_audits_grouped_workflows' },
  { key: 'proba', label: 'Proba Analytics', endpoint: 'get_audits_grouped_proba' },
  { key: 'all', label: 'All platforms', endpoint: 'get_audits_grouped_all' },
];

function initials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  return parts.length >= 2
    ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    : parts[0].slice(0, 2).toUpperCase();
}

export default {
  name: 'AuditTrailView',
  components: { Sidebar, Navbar },

  data: () => ({
    grouped_users: [],
    active_platform: 'aic_one',
    _last_fetched_platform: '',
    platform_tabs: PLATFORM_TABS,
    pagination: {
      total: 0,
      num_pages: 0,
      current_page: 1,
      page_size: 20,
      has_next: false,
      has_previous: false,
      next_page: null,
      previous_page: null,
    },
    stats: {
      total_events: 0,
      high_critical: 0,
      platform_count: 0,
      active_users: 0,
    },
    expanded_users: new Set(),
    user_event_pages: {},
    search_query: '',
    filter_risk: '',
    filter_date_range: '',
    _search_timer: null,
    csrf_token: '',
    is_loading: false,
    table_key: 0,
  }),

  computed: {
    has_active_filters() {
      return !!(this.search_query || this.filter_risk || this.filter_date_range);
    },

    stat_cards() {
      return [
        { label: 'Total Events', value: this.stats.total_events, val_class: '' },
        { label: 'Active Users', value: this.stats.active_users, val_class: '' },
        { label: 'High', value: this.stats.high_critical, val_class: 'text-danger' },
      ];
    },

    page_range() {
      const total = this.pagination.num_pages;
      const cur = this.pagination.current_page;
      if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
      if (cur <= 4) return [1, 2, 3, 4, 5, '...', total];
      if (cur >= total - 3) return [1, '...', total - 4, total - 3, total - 2, total - 1, total];
      return [1, '...', cur - 1, cur, cur + 1, '...', total];
    },

    display_groups() {
      return (this.grouped_users || []).map(group => {
        return {
          ...group,
          initials: initials(group.user_full_names),
        };
      });
    },
  },

  mounted() {
    this.csrf_token = getCookie('csrftoken');
    this.fetch_for_platform('aic_one', 1);
  },

  methods: {
    switch_platform(key) {
      if (key === this._last_fetched_platform && !this.is_loading) return;
      this._last_fetched_platform = key;
      this.expanded_users = new Set();
      this.user_event_pages = {};
      this.fetch_for_platform(key, 1);
    },

    async fetch_for_platform(platform_key, page = 1) {
      const tab = PLATFORM_TABS.find(t => t.key === platform_key);
      if (!tab) return;
      this.is_loading = true;

      const params = new URLSearchParams({ page, page_size: this.pagination.page_size });
      if (this.search_query.trim()) params.set('search', this.search_query.trim());
      if (this.filter_risk) params.set('risk_level', this.filter_risk);
      if (this.filter_date_range) this.build_date_params(params);

      if (!PLATFORM_TABS.some(t => t.endpoint === tab.endpoint)) {
        this.$notyf?.error('Invalid endpoint');
        this.is_loading = false;
        return;
      }

      const url = `${process.env.VUE_APP_API_BASE_URL}/aic_one_api/system_management/${tab.endpoint}/?${params}`;

      try {
        const res = await fetch(url, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Token ${get_token()}`,
            'X-CSRFToken': this.csrf_token,
          },
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok || json.status !== 'success') {
          this.$notyf?.error(json.message || `Error ${res.status}`);
          return;
        }
        this.grouped_users = json.data || [];
        this.pagination = {
          ...this.pagination,
          ...json.pagination
        };
        this.user_event_pages = {};
        if (json.stats) this.stats = {
          ...this.stats,
          ...json.stats
        };
      } catch {
        this.$notyf?.info('Network error fetching audit logs');
      } finally {
        this.is_loading = false;
      }
    },

    refetch() {
      this.fetch_for_platform(this.active_platform, 1);
    },

    on_search_input() {
      clearTimeout(this._search_timer);
      this._search_timer = setTimeout(() => this.refetch(), 350);
    },

    clear_filters() {
      this.search_query = '';
      this.filter_risk = '';
      this.filter_date_range = '';
      this.refetch();
    },

    build_date_params(params) {
      const now = new Date();
      const pad = d => d.toISOString().split('T')[0];
      if (this.filter_date_range === 'today') {
        params.set('date_from', pad(now));
        params.set('date_to', pad(now));
      } else if (this.filter_date_range === 'week') {
        const d = new Date(now);
        d.setDate(d.getDate() - 7);
        params.set('date_from', pad(d));
        params.set('date_to', pad(now));
      } else if (this.filter_date_range === 'month') {
        const d = new Date(now);
        d.setDate(d.getDate() - 30);
        params.set('date_from', pad(d));
        params.set('date_to', pad(now));
      }
    },

    next_page() {
      if (this.pagination.has_next && this.pagination.next_page) {
        this.fetch_for_platform(this.active_platform, this.pagination.next_page);
      }
    },

    previous_page() {
      if (this.pagination.has_previous && this.pagination.previous_page) {
        this.fetch_for_platform(this.active_platform, this.pagination.previous_page);
      }
    },

    toggle_user(email) {
      const s = new Set(this.expanded_users);
      if (s.has(email)) {
        s.delete(email);
      } else {
        s.add(email);
      }
      this.expanded_users = s;
    },

    get_user_event_page(email) {
      return Reflect.get(this.user_event_pages, email) || 1;
    },

    set_user_event_page(email, page) {
      const updated = { ...this.user_event_pages };
      Reflect.set(updated, email, page);
      this.user_event_pages = updated;
    },

    user_total_pages(events) {
      return Math.ceil((events?.length || 0) / USER_PAGE_SIZE);
    },

    user_event_start(email) {
      return (this.get_user_event_page(email) - 1) * USER_PAGE_SIZE + 1;
    },

    user_event_end(email, events) {
      return Math.min(this.get_user_event_page(email) * USER_PAGE_SIZE, events?.length || 0);
    },

    user_next_page(email, events) {
      if (this.get_user_event_page(email) < this.user_total_pages(events)) {
        this.set_user_event_page(email, this.get_user_event_page(email) + 1);
      }
    },

    user_prev_page(email) {
      if (this.get_user_event_page(email) > 1) {
        this.set_user_event_page(email, this.get_user_event_page(email) - 1);
      }
    },

    user_go_to_page(email, page) {
      this.set_user_event_page(email, page);
    },

    paginated_events(email, events) {
      if (!events?.length) return [];
      const start = (this.get_user_event_page(email) - 1) * USER_PAGE_SIZE;
      return events.slice(start, start + USER_PAGE_SIZE);
    },

    user_page_range(email, events) {
      const total = this.user_total_pages(events);
      const cur = this.get_user_event_page(email);
      if (total <= 7)
        return Array.from({ length: total }, (_, i) => i + 1);

      if (cur <= 4)
        return [1, 2, 3, 4, 5, '...', total];

      if (cur >= total - 3)
        return [1, '...', total - 4, total - 3, total - 2, total - 1, total];

      return [1, '...', cur - 1, cur, cur + 1, '...', total];
    },

    format_datetime(raw) {
      if (!raw) return { date: '—', time: '' };
      const str = String(raw).replace('T', ' ');
      const parts = str.split(' ');
      return ({ date: parts[0] || '', time: (parts[1] || '').split('.')[0] });
    },

    go_to_page(url) {
      if (!url) {
        this.$notyf?.info?.('No page URL available');
        return;
      }
      let pathname = '';
      try {
        pathname = new URL(url, window.location.origin).pathname;
      } catch {
        pathname = url;
      }
      if (pathname === '/' || /\/(login|logout)\b/i.test(pathname)) {
        this.$notyf?.info?.('Login event — nothing to open.');
        return;
      }
      window.location.assign(url);
    },
  },
};